'use client';

import { createContext, useContext, useMemo } from 'react';

import { setConfig } from '../services/config';
import getLocalisationConfig from '../services/getLocalisationConfig';
import type { LocalisationConfigContextType, LocalisationConfig } from '../types';

export const LocalisationConfigContext = createContext<LocalisationConfigContextType | null>(null);

export function LocalisationConfigProvider({
  children,
  config,
}: Readonly<{
  children: React.ReactNode;
  config: Partial<LocalisationConfig & { locale: string }>;
}>) {
  /**
   * Initialize client localisation configuration to use like this:
   * @example
   * import { getLocalisationConfig } from '@openclassrooms/localisation/services';
   * const videoUrl = getLocalisationConfig('aboutUs.description.video');
   */
  useMemo(() => {
    setConfig(config);
  }, [config]);

  return (
    /**
     * Allow usage of useLocalisationConfig hook
     * @example
     * import { useLocalisationConfig } from '@openclassrooms/localisation/LocalisationConfigProvider';
     * const { getLocalisationConfig } = useLocalisationConfig();
     * const videoUrl = getLocalisationConfig('aboutUs.description.video');
     */
    <LocalisationConfigContext.Provider value={{ configuration: config, getLocalisationConfig }}>
      {children}
    </LocalisationConfigContext.Provider>
  );
}

export const useLocalisationConfig = (): LocalisationConfigContextType => {
  const context = useContext(LocalisationConfigContext);

  if (context == null) {
    throw new Error('useLocalisationConfig hook was used without LocalisationConfigProvider.');
  }

  return context;
};
